// src/utils/tagExtraction.ts
import { BlogPost } from '../types/blog';

interface TagExtractionCache {
  [url: string]: {
    tags: string[];
    timestamp: number;
  };
}

const tagCache: TagExtractionCache = {};
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

export const extractTagsFromHTML = async (htmlContent: string): Promise<string[]> => {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    
    // Extract all SEO tags from .tag class elements
    const tagElements = doc.querySelectorAll('.tag');
    let tags = Array.from(tagElements).map(el => el.textContent?.trim() || '');
    
    // Remove empty tags and duplicates
    tags = [...new Set(tags.filter(tag => tag.length > 0))];
    
    // Limit to first 4 tags that fit well in UI
    const maxTags = 4;
    const limitedTags = tags.slice(0, maxTags);
    
    return limitedTags;
  } catch (error) {
    console.error('Error extracting tags from HTML:', error);
    return [];
  }
};

export const extractTagsFromS3 = async (s3Url: string): Promise<string[]> => {
  // Check cache first
  const cached = tagCache[s3Url];
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.tags;
  }

  try {
    const response = await fetch(s3Url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const htmlContent = await response.text();
    const extractedTags = await extractTagsFromHTML(htmlContent);
    
    // Cache the result
    tagCache[s3Url] = {
      tags: extractedTags,
      timestamp: Date.now()
    };
    
    return extractedTags;
  } catch (error) {
    console.error(`Error fetching tags from S3 URL ${s3Url}:`, error);
    return [];
  }
};

export const getTagsForBlogPost = async (post: BlogPost): Promise<string[]> => {
  // Try to get extracted tags from S3 first
  const extractedTags = await extractTagsFromS3(post.s3Url);
  
  if (extractedTags.length > 0) {
    return extractedTags;
  }
  
  // Fallback to static tags if extraction fails
  return post.tags || ['market', 'analysis', 'daily'];
};

export const clearTagCache = (): void => {
  Object.keys(tagCache).forEach(key => delete tagCache[key]);
};